import { useState } from 'react';

import { type ReviewCycleSummaryResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  PageHeader,
  Panel,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import { formatDate, formatInteger } from '../../format';
import {
  useBuildCyclePreview,
  useCyclePreview,
  useLateArrivals,
  useReviewCycle,
  useReviewQueue,
  useUpcomingMovements,
  type CycleTarget,
} from '../queries';
import { CycleGatePanel } from './CycleGatePanel';
import { ReviewDocumentPanel } from './ReviewDocumentPanel';
import { ReviewItemsPanel } from './ReviewItemsPanel';
import { REVIEW_COPY, currentCycleMonth } from './reviewQueue';

/**
 * The vyttech back-office: what is taking effect this cycle, and the sign-off.
 *
 * This is the human double-check vyttech performs today, reshaped. Today
 * somebody reads every row of every customer's spreadsheet before the file goes
 * out; here they read the flagged ones, decide those, look at the file the
 * cutoff would produce, and approve it.
 *
 * The screen is organised the way the work is: contracts, then one contract's
 * cycle, then the gate. Three listings hang off the cycle and they are kept
 * apart on purpose --
 *
 *  - **this cycle**, which is what the file will contain, carry-over included;
 *  - **later cycles**, so the pipeline ahead is visible rather than a surprise
 *    each cutoff; and
 *  - **late arrivals**, movements dated inside a month the insurer already has,
 *    which are shown loudly, are never counted as part of this cycle, and carry
 *    no correction path at all -- what the insurer accepts by way of an
 *    amendment is not something this screen should invent.
 *
 * The document panel sits beside the table rather than on a route of its own,
 * because confirming a date against the signed agreement means having both in
 * front of you and nobody checks a date twice if checking it costs a page load.
 *
 * **The preview follows the decisions.** Once somebody has built the file panel's
 * draft, every decision on a movement of this cycle rebuilds it, so the digest
 * the approve button would sign is always the one the cutoff now produces. A
 * cycle nobody has previewed is left alone: deciding items there must not start
 * building drafts nobody asked for. The page owns the build for that reason --
 * the listing only says a decision was recorded, and the gate only shows what
 * the build is doing.
 *
 * Everything here reaches the backend through the generated client, and every
 * endpoint it calls requires `review:read` or `review:write` -- which no
 * customer role and no machine credential holds. What stops a customer's HR user
 * from reading this is the server, not the router.
 */
export function ReviewQueuePage() {
  const [cycleMonth, setCycleMonth] = useState(currentCycleMonth());
  const [contractId, setContractId] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);

  const queue = useReviewQueue(cycleMonth);
  const cycle = useReviewCycle(contractId, cycleMonth);
  const upcoming = useUpcomingMovements(contractId, cycleMonth);
  const late = useLateArrivals(contractId, cycleMonth);
  const preview = useCyclePreview(contractId, cycleMonth);
  const build = useBuildCyclePreview();
  const { notify } = useNotifications();
  // Set when a rebuild after a decision failed, so the draft on screen is older
  // than the cycle; cleared by any build that succeeds.
  const [previewIsStale, setPreviewIsStale] = useState(false);

  const forgetBuild = () => {
    build.reset();
    setPreviewIsStale(false);
  };

  const buildPreview = (target: CycleTarget, afterDecision: boolean) =>
    build.mutate(target, {
      onSuccess: () => {
        setPreviewIsStale(false);
        notify(
          'success',
          afterDecision ? REVIEW_COPY.decidedPreviewUpdated : REVIEW_COPY.previewBuilt,
        );
      },
      onError: () => {
        if (afterDecision) {
          setPreviewIsStale(true);
        }
      },
    });

  const rebuildAfterDecision = (target: CycleTarget) => {
    // `data`, not `isSuccess`: a 404 is "nobody has previewed this yet", and
    // that cycle keeps its "Gerar prévia" button rather than gaining a draft.
    if (preview.data !== undefined) {
      buildPreview(target, true);
    }
  };

  const columns: readonly Column<ReviewCycleSummaryResponse>[] = [
    { key: 'company', header: 'Cliente', render: (row) => row.company_name },
    {
      key: 'insurer',
      header: 'Operadora',
      render: (row) => (
        <>
          {row.insurer_name}
          <br />
          <span className="table__muted">
            {row.subestipulante} / {row.modulo}
          </span>
        </>
      ),
    },
    { key: 'cutoff', header: 'Corte', render: (row) => formatDate(row.cutoff_date) },
    {
      key: 'items',
      header: 'Neste ciclo',
      align: 'end',
      render: (row) => formatInteger(row.item_count),
    },
    {
      key: 'blocking',
      header: 'Impeditivas',
      align: 'end',
      render: (row) =>
        row.blocking_count === 0 ? (
          <span className="table__muted">0</span>
        ) : (
          <span className="badge badge--blocking">{formatInteger(row.blocking_count)}</span>
        ),
    },
    {
      key: 'upcoming',
      header: 'Ciclos futuros',
      align: 'end',
      render: (row) => formatInteger(row.upcoming_count),
    },
    {
      key: 'late',
      header: 'Fora do período',
      align: 'end',
      render: (row) =>
        row.late_arrival_count === 0 ? (
          <span className="table__muted">0</span>
        ) : (
          <span className="badge badge--warning">{formatInteger(row.late_arrival_count)}</span>
        ),
    },
    {
      key: 'status',
      header: 'Situação',
      render: (row) => (row.is_approved ? 'Aprovado' : 'Em análise'),
    },
    {
      key: 'open',
      header: 'Ações',
      align: 'end',
      render: (row) => (
        <button
          type="button"
          className="button button--secondary"
          onClick={() => {
            setContractId(row.contract_id);
            setDocumentId(null);
            forgetBuild();
          }}
        >
          Analisar
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader title={REVIEW_COPY.title} description={REVIEW_COPY.description} />

      <Panel title={REVIEW_COPY.contractsTitle} description={REVIEW_COPY.contractsDescription}>
        <TextField
          label={REVIEW_COPY.cycleLabel}
          hint="O mês cujo fechamento você está analisando."
          type="month"
          value={cycleMonth}
          onChange={(value) => {
            setCycleMonth(value);
            setDocumentId(null);
            forgetBuild();
          }}
        />
        <AsyncSection
          state={queue}
          loadingLabel="Carregando a fila…"
          errorTitle="Não foi possível carregar a fila"
          isEmpty={(data) => data.items.length === 0}
          emptyTitle="Nenhum contrato"
          emptyDescription={REVIEW_COPY.noContracts}
        >
          {(data) => (
            <DataTable
              id="review-queue"
              caption="Contratos com movimentações em análise"
              columns={columns}
              rows={data.items}
              rowKey={(row) => row.contract_id}
              emptyTitle="Nenhum contrato"
            />
          )}
        </AsyncSection>
      </Panel>

      {contractId !== null && (
        <div className="review__columns">
          <div className="review__work">
            <AsyncSection
              state={cycle}
              loadingLabel="Carregando o ciclo…"
              errorTitle="Não foi possível carregar o ciclo"
            >
              {(data) => {
                const target: CycleTarget = {
                  contractId: data.contract_id,
                  companyId: data.company_id,
                  cycleMonth: data.cycle_month,
                };
                return (
                  <>
                    <Panel
                      title={REVIEW_COPY.currentTitle}
                      description={`Corte de ${formatDate(data.cutoff_date)} · ${formatInteger(
                        data.item_count,
                      )} movimentação(ões), ${formatInteger(data.blocking_count)} impeditiva(s).`}
                    >
                      <ReviewItemsPanel
                        items={data.items}
                        companyId={data.company_id}
                        decidable={!data.is_approved}
                        onOpenDocument={setDocumentId}
                        onDecided={() => rebuildAfterDecision(target)}
                        emptyTitle={REVIEW_COPY.noItems}
                      />
                    </Panel>

                    <CycleGatePanel
                      cycle={data}
                      build={{
                        start: () => buildPreview(target, false),
                        isBuilding: build.isPending,
                        error: build.error,
                        isStale: previewIsStale,
                      }}
                    />
                  </>
                );
              }}
            </AsyncSection>

            <Panel title={REVIEW_COPY.upcomingTitle} description={REVIEW_COPY.upcomingDescription}>
              <AsyncSection
                state={upcoming}
                loadingLabel="Carregando…"
                errorTitle="Não foi possível carregar as movimentações futuras"
              >
                {(data) => (
                  <ReviewItemsPanel
                    items={data.items}
                    companyId={cycle.data?.company_id ?? ''}
                    decidable={false}
                    showCycle
                    onOpenDocument={setDocumentId}
                    emptyTitle={REVIEW_COPY.noUpcoming}
                  />
                )}
              </AsyncSection>
            </Panel>

            <Panel title={REVIEW_COPY.lateTitle} description={REVIEW_COPY.lateDescription}>
              <AsyncSection
                state={late}
                loadingLabel="Carregando…"
                errorTitle="Não foi possível carregar as movimentações fora do período"
              >
                {(data) => (
                  <ReviewItemsPanel
                    items={data.items}
                    companyId={cycle.data?.company_id ?? ''}
                    decidable={false}
                    showCycle
                    onOpenDocument={setDocumentId}
                    emptyTitle={REVIEW_COPY.noLate}
                  />
                )}
              </AsyncSection>
            </Panel>
          </div>

          {documentId !== null && cycle.data !== undefined && (
            <aside className="review__aside">
              <ReviewDocumentPanel
                documentId={documentId}
                companyId={cycle.data.company_id}
                onClose={() => setDocumentId(null)}
              />
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
